import React, {useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import {noop} from '@priority-software/priority-style-react';
import {isRTL} from '../../../../../../Localization';
import {getValidNewWidth, getValidOffset} from './utils';

type Props = {
    onResizeEnd?: (width: number, columnId: number) => void;
    columnId: number;
    width: number;
    minWidth: number;
    maxWidth: number;
};

export const CellResizeHandle = (props: Props) => {
    const {onResizeEnd, columnId, width, minWidth, maxWidth} = props;
    const [isDragging, setIsDragging] = useState(false);
    const startXPoint = useRef(0);
    const startYPoint = useRef(0);
    const handleRef = useRef<HTMLDivElement>(null);

    const onMouseMove = useCallback(
        e => {
            const handleEl = handleRef.current;
            if (handleEl) {
                const diff = e.clientX - startXPoint.current;
                const validatedDiff = getValidOffset(diff, width, minWidth, maxWidth);
                handleEl.style.transform = `translateX(${validatedDiff}px)`;
            }
        },
        [width, minWidth, maxWidth]
    );

    const onMouseUp = useCallback(
        e => {
            const diff = !isRTL()
                ? e.clientX - startXPoint.current
                : startXPoint.current - e.clientX;

            const newWidth = width + Math.round(diff);
            const validatedWidth = getValidNewWidth(newWidth, minWidth, maxWidth);

            if (onResizeEnd && newWidth !== width) {
                onResizeEnd(validatedWidth, columnId);
            }

            startXPoint.current = 0;
            startYPoint.current = 0;

            const handleEl = handleRef.current;
            if (handleEl) {
                handleEl.style.transform = null;
            }
            setIsDragging(false);
            document.querySelector('.react-screen-wrapper-inner').classList.remove(styles.blocked);
        },
        [onResizeEnd, columnId, width, maxWidth, minWidth]
    );

    const onMouseDown = useCallback(e => {
        startXPoint.current = e.clientX;
        startYPoint.current = e.clientY;
        setIsDragging(true);
        document.querySelector('.react-screen-wrapper-inner').classList.add(styles.blocked);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('mousemove', onMouseMove);

            return () => {
                window.removeEventListener('mouseup', onMouseUp);
                window.removeEventListener('mousemove', onMouseMove);
            };
        }
        return noop;
    }, [isDragging]);

    return (
        <div className={cx(styles.component, {[styles.isDragging]: isDragging})}>
            <div
                ref={handleRef}
                className={styles.handle}
                onMouseUp={onMouseUp}
                onMouseDown={onMouseDown}
            >
                <div className={styles.border} />
            </div>
        </div>
    );
};
